"use client"

import { useRef, useEffect, useState } from "react"
import { Maximize2, RotateCcw, X, UserRound } from "lucide-react"
import Link from "next/link"
import ChatbotMessage from "./ChatbotMessage"
import ChatbotInput from "./ChatbotInput"
import ChatbotSuggestions from "./ChatbotSuggestions"
import ChatbotConnectForm from "./ChatbotConnectForm"
import type { ChatbotMessageUI, ChatbotAction } from "@/lib/chatbot/types"
import { cn } from "@/lib/utils"

interface ChatbotPanelProps {
  variant: "widget" | "fullpage"
  messages: ChatbotMessageUI[]
  isStreaming: boolean
  error: string | null
  onSend: (text: string) => void
  onStop: () => void
  onClear: () => void
  onAction: (action: ChatbotAction) => void
  onAddBotMessage: (content: string) => void
  onClose?: () => void
}

export default function ChatbotPanel({
  variant,
  messages,
  isStreaming,
  error,
  onSend,
  onStop,
  onClear,
  onAction,
  onAddBotMessage,
  onClose,
}: ChatbotPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showConnectForm, setShowConnectForm] = useState(false)
  const showSuggestions = messages.length <= 2

  // The last bot message that's done streaming (not the initial greeting)
  const lastBotMsg = [...messages]
    .reverse()
    .find((m) => m.role === "assistant" && !m.isStreaming && m.id !== "initial")

  const showConnectButton = !!lastBotMsg && !isStreaming && !showConnectForm

  // Auto-scroll to bottom on new messages or when connect form appears
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, showConnectForm])

  return (
    <div
      className={cn(
        "flex flex-col bg-background",
        variant === "widget" ? "h-full rounded-2xl border border-border shadow-xl" : "h-full"
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between border-b border-border px-4 py-3",
          variant === "widget" && "rounded-t-2xl"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary">
            <span className="text-xs font-bold text-primary-foreground">P</span>
          </div>
          <div>
            <p className="text-sm font-semibold leading-none">PhantomCodeAI</p>
            <p className="text-xs text-muted-foreground">Support</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onClear}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Clear chat"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          {variant === "widget" && (
            <>
              <Link
                href="/chatbot"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title="Open full page"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Link>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className={cn(
          "flex-1 overflow-y-auto space-y-3",
          variant === "fullpage" ? "px-4 py-4 sm:px-6" : "px-3 py-3"
        )}
      >
        {messages.map((msg) => {
          if (msg.isStreaming && !msg.content) return null
          return <ChatbotMessage key={msg.id} message={msg} onAction={onAction} />
        })}

        {/* Typing indicator */}
        {isStreaming && messages[messages.length - 1]?.content === "" && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5">
              <div className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40 [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40 [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40" />
              </div>
            </div>
          </div>
        )}

        {/* Connect with someone button — after every completed bot response */}
        {showConnectButton && (
          <div className="flex justify-start">
            <button
              onClick={() => setShowConnectForm(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <UserRound className="h-3 w-3" />
              Connect with someone
            </button>
          </div>
        )}

        {/* Connect form */}
        {showConnectForm && (
          <ChatbotConnectForm
            onSubmitted={(userName) => {
              setShowConnectForm(false)
              onAddBotMessage(
                `Thank you ${userName}! We've received your details. Our team will connect with you via email within 24 hours.`
              )
            }}
          />
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-3 mb-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Suggestions — only in widget, fullpage has its own hero suggestions */}
      {showSuggestions && variant === "widget" && (
        <ChatbotSuggestions onSelect={onSend} disabled={isStreaming} />
      )}

      {/* Input */}
      <ChatbotInput onSend={onSend} onStop={onStop} isStreaming={isStreaming} />
    </div>
  )
}
